import {
  selectFinancialYear,
  selectPartnerId,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { FC, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { LoaderOverlay } from 'core-components/library/loader'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import { MeasureController } from 'core/analytics'

import {
  requestWizardSteps,
  selectWizardActiveStepId,
  selectWizardIsLoading,
  selectWizardSteps,
} from '../../features/planningWizard'
import { isWizardStepsGroup } from '../../helpers/isWizardStepsGroup'
import { StepRenderer } from '../stepRenderer/StepRenderer'
import { WizardStep } from '../wizardStep/WizardStep'
import { WizardStepGroup } from '../wizardStepGroup/WizardStepGroup'

import { WizardHeader } from './components/wizardHeader/WizardHeader'

import styles from './WizardContent.module.scss'

interface IWizardContentProps {
  pid: string
  onClose: () => void

  widgetParams: IObjectAny
  flexWidth: FlexDirectionBreak
  metrics: MeasureController
}

export const WizardContent: FC<IWizardContentProps> = ({
  pid,
  onClose,
  widgetParams,
  flexWidth,
  metrics,
}) => {
  /** ------------------------- Selectors ------------------------- */

  const wizardSteps = useSelector(selectWizardSteps)
  const isLoading = useSelector(selectWizardIsLoading)
  const activeStepId = useSelector(selectWizardActiveStepId)
  const partnerId = useSelector(selectPartnerId)
  const fYear = useSelector(selectFinancialYear)
  const programType = useSelector(selectProgramType)

  /** ------------------------- Hooks ------------------------- */

  const dispatch = useAppDispatch()

  /** ------------------------- Effects ------------------------- */

  useEffect(() => {
    if (partnerId && fYear && programType) {
      dispatch(
        requestWizardSteps({
          partnerId,
          fYear,
          programType,
        })
      )
    }
  }, [partnerId, fYear, programType, dispatch])

  /** ------------------------- Variables ------------------------- */

  const activeStep = useMemo(() => {
    const flat = wizardSteps.flatMap((step) => (isWizardStepsGroup(step) ? step.steps : [step]))

    return flat.find((s) => s.id === activeStepId) || null
  }, [wizardSteps, activeStepId])

  /** ------------------------- Memoized Render ------------------------- */

  const renderedSteps = useMemo(
    () =>
      wizardSteps.map((step) => {
        if (isWizardStepsGroup(step)) {
          return <WizardStepGroup key={step.id} step={step} activeStepId={activeStepId} />
        }

        return <WizardStep key={step.id} step={step} activeStepId={activeStepId} />
      }),
    [wizardSteps, activeStepId]
  )

  /** ------------------------- Render ------------------------- */

  return (
    <div className={styles.wizardRoot}>
      <WizardHeader
        onClose={onClose}
        widgetParams={widgetParams}
        pid={pid}
        activeStep={activeStep}
      />

      <div className={styles.wizardContent}>
        <div className={styles.wizardSidebar}>
          <div className={styles.sidebarSteps}>{renderedSteps}</div>
        </div>

        <div className={styles.right}>
          <div className={styles.contentBody}>
            <LoaderOverlay show={isLoading} />
            {!!activeStepId && (
              <StepRenderer
                steps={wizardSteps}
                activeId={activeStepId}
                widgetParams={widgetParams}
                flexWidth={flexWidth}
                metrics={metrics}
                pid={pid}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
